import { useCallback, useEffect } from 'react'
import {
  Text,
  Button,
  Heading,
  Paragraph,
  Flex,
  UnstyledButton,
  Loader,
} from '@buffer-mono/popcorn'

import { isValidChannelCountForPlan } from '../../../../../common/utils/validation'

import { PlanContext } from '../../../../../common/context/Plan'
import useChannelsCounter from '../../../../../common/hooks/ChannelsCounter/useChannelsCounter'
import useUpdateSubscriptionQuantity from '../../../../../common/hooks/Subscription/useUpdateSubscriptionQuantity'
import Channels from '../../../../../common/components/Channels/Channels'
import useGetTotalChargeAmount from '../../../../../common/hooks/ChargeAmount/useGetTotalChargeAmount'
import { calculateTotalSlotsPrice } from '../../../utils'
import {
  trackQuantityUpdateModalViewed,
  formatCTAString,
} from '../../../../../common/hooks/useSegmentTracking'

import type { CardBodyProps } from './CardBodyTypes'
import { isBillingResponse } from '../../../../../common/types/graphQL/Billing'
import type { ModalData } from '../../../../../common/events/types'
import { MODALS } from '../../../../../common/types'
import { useModalManager } from '../../../../ModalManager/hooks/useModalManager'

import styles from './CardBody.module.css'

const CardBody = ({
  planName,
  quantity,
  channelFee,
  pricePerQuantity,
  minimumQuantity,
  planId,
  planCycle,
  user,
  trialInfo,
}: CardBodyProps): JSX.Element => {
  const {
    channelsCount,
    increaseCounter,
    decreaseCounter,
    channelCountMessageStatus,
    setChannelsCounterValue,
  } = useChannelsCounter(planId, quantity, minimumQuantity)

  const {
    modalData: data,
    openModal,
    dismissModal: closeModal,
  } = useModalManager()
  const cta = data?.cta || ''
  const upgradePathName = data?.upgradePathName || ''

  const newPrice = calculateTotalSlotsPrice(
    planId,
    channelsCount,
    pricePerQuantity,
    minimumQuantity,
    channelFee,
  )

  const currentPrice = calculateTotalSlotsPrice(
    planId,
    quantity,
    pricePerQuantity,
    minimumQuantity,
    channelFee,
  )

  const hasCounterChanged = channelsCount !== quantity

  const { loading, totalAmountDue, nextBillingDate, isScheduledChange } =
    useGetTotalChargeAmount(
      user,
      planId,
      planCycle,
      channelsCount,
      planId,
      newPrice,
      trialInfo?.isActive || false,
      hasCounterChanged,
    )

  const totalDueHeader = isScheduledChange
    ? 'Total due on ' + nextBillingDate
    : 'Total due today'

  const ctaButtonText = isScheduledChange
    ? 'Confirm Plan Change'
    : 'Confirm and Pay'

  const {
    updateSubscriptionQuantity,
    data: updateQuantityData,
    isProcessing,
    error,
  } = useUpdateSubscriptionQuantity({
    user,
    channelsQuantity: channelsCount,
    cta,
    upgradePathName,
  })

  const isValidChannelsCount = isValidChannelCountForPlan(
    planId,
    channelsCount,
    minimumQuantity,
  )

  const disableSumbitButton =
    !hasCounterChanged || isProcessing || !isValidChannelsCount

  const planInfoContext = {
    selectedPlanId: planId,
    selectedPlanName: planName,
    selectedPlanMinimumQuantity: minimumQuantity,
    currentPlanChannelQuantity: quantity,
  }

  const openSuccessModal = useCallback(
    (newData: ModalData): void => {
      closeModal()
      openModal({ key: MODALS.success, data: newData })
    },
    [openModal],
  )

  useEffect(() => {
    trackQuantityUpdateModalViewed({
      payload: {
        currentPlan: formatCTAString(`${planId} ${planCycle}`),
        cta,
        ctaButton: 'confimAndPay',
        ctaView: 'quantityUpdateModal',
        ctaLocation: 'quantityUpdateModal',
        upgradePathName: 'quantityUpdateModal-upgrade',
      },
      user,
    })
  }, [])

  useEffect(() => {
    if (
      isBillingResponse(
        updateQuantityData?.billingUpdateSubscriptionQuantity,
      ) &&
      updateQuantityData?.billingUpdateSubscriptionQuantity?.success
    ) {
      openSuccessModal({
        selectedPlan: planName,
        stayedOnSamePlan: true,
        isDowngradingToFreePlan: false,
        scheduledUpdate:
          updateQuantityData.billingUpdateSubscriptionQuantity.scheduledUpdate,
      })
    }
  }, [updateQuantityData, openSuccessModal, planName])

  const modalData = {
    cta: 'quantityUpdateModal-paragraph-changeplan-1',
    ctaButton: 'changeplan',
    ctaView: 'quantityUpdateModal',
    ctaLocation: 'quantityUpdateModal-paragraph',
    upgradePathName: 'quantityUpdateModal-changeplan',
  }

  return (
    <PlanContext.Provider value={planInfoContext}>
      <>
        <div className={styles.header}>
          <Heading as="h2" size="medium">
            Add or Remove Channels from Plan
          </Heading>
          <Paragraph className={styles.description}>
            You&apos;re currently on the <strong>{planName}</strong> plan and
            you&apos;re paying{' '}
            <strong>
              ${pricePerQuantity}/{planCycle} per channel
            </strong>{' '}
            for {quantity} channel
            {quantity !== 1 ? 's' : ''}.{' '}
            <UnstyledButton
              data-testid="quantityupdate-changeplan"
              className={styles.changePlanButton}
              onClick={(): void => {
                closeModal()
                openModal({ key: MODALS.planSelector, data: modalData })
              }}
            >
              Change Plan
            </UnstyledButton>
          </Paragraph>
          {error && <Text className={styles.errorText}>{error.message}</Text>}
        </div>
        <div className={styles.sectionContainer}>
          <div className={styles.section}>
            <Flex className={styles.innerContainer}>
              <div className={styles.channelsWrapper}>
                <Channels
                  channelsCount={channelsCount}
                  onIncreaseCounter={(): void => increaseCounter()}
                  onDecreaseCounter={(): void => decreaseCounter()}
                  channelCounterMessageStatus={channelCountMessageStatus}
                  setChannelsCounterValue={(value: number): void =>
                    setChannelsCounterValue(value)
                  }
                  isSmallLayout={true}
                />
              </div>

              <div className={styles.summary}>
                <Paragraph>Currently paying: ${currentPrice}</Paragraph>
                {hasCounterChanged ? (
                  <>
                    <Flex className={styles.totalCost}>
                      <Paragraph>
                        {!loading && (
                          <>
                            <strong>{totalDueHeader}:</strong> ${totalAmountDue}
                          </>
                        )}
                      </Paragraph>

                      {loading && (
                        <div className={styles.loaderWrapper}>
                          {' '}
                          <Loader />
                        </div>
                      )}
                    </Flex>
                    {!loading && (
                      <Paragraph className={styles.billingInfo}>
                        <span>
                          {isScheduledChange
                            ? `Starting on ${nextBillingDate}, `
                            : ''}
                          ${newPrice} + Applicable taxes
                        </span>{' '}
                        will be billed every {planCycle} until canceled.
                      </Paragraph>
                    )}
                  </>
                ) : (
                  <Paragraph>
                    <strong>+ ${pricePerQuantity}</strong> per channel
                  </Paragraph>
                )}
              </div>
            </Flex>
          </div>
        </div>

        <Flex className={styles.buttonWrapper} justify="end" gap="sm">
          <Button variant="secondary" onClick={() => closeModal()}>
            Cancel
          </Button>
          <Button
            id="confirm_and_pay"
            variant="primary"
            onClick={(): void => {
              updateSubscriptionQuantity()
            }}
            disabled={disableSumbitButton}
          >
            {isProcessing ? 'Processing...' : ctaButtonText}
          </Button>
        </Flex>
      </>
    </PlanContext.Provider>
  )
}

export default CardBody
